import React, { useEffect, useRef, useState } from 'react';
import { TextInput } from '@fabric-ui/components';
import { MinusCircle, Plus, Vector } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { iconSize, isDeepEqual } from '@fabric-ui/utils';
import { Box, Button, FormControl, IconButton, InputLabel } from '@mui/material';

const fieldArrow = (
  <Vector style={{ height: '22px', width: '12px', margin: '0 16px', fill: 'var(--dataviz-color-sequential-blue-scale03)' }} />
);

const plusIcon = <Plus style={iconSize(16)} />;

type Pair = { id: string; k: string; v: string };

const createPair = (k = '', v = ''): Pair => ({ id: crypto.randomUUID(), k, v });

const toPairs = (obj: Record<string, string>): Pair[] => {
  const entries = Object.entries(obj || {});
  return entries.length ? entries.map(([k, v]) => createPair(k, v == null ? '' : String(v))) : [createPair()];
};

interface DictionaryProps {
  value?: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  label?: React.ReactNode;
  addButtonText?: string;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  disabled?: boolean;
  isRequired?: boolean;
}

const Dictionary = ({
  value = {},
  onChange,
  label,
  addButtonText = 'Add',
  keyPlaceholder = 'Add key...',
  valuePlaceholder = 'Add value...',
  disabled = false,
  isRequired = false,
}: DictionaryProps) => {
  const [pairs, setPairs] = useState<Pair[]>(() => toPairs(value));
  const lastEmitted = useRef<Record<string, string>>(value);

  useEffect(() => {
    if (!isDeepEqual(value, lastEmitted.current)) {
      setPairs(toPairs(value));
      lastEmitted.current = value;
    }
  }, [value]);

  const emit = (next: Pair[]) => {
    setPairs(next);
    const obj = next.reduce((acc, { k, v }) => (k ? { ...acc, [k]: v } : acc), {} as Record<string, string>);
    lastEmitted.current = obj;
    onChange(obj);
  };

  const isLastRow = pairs.length === 1;
  const setKey = (i: number, k: string) => emit(pairs.map((p, idx) => (idx === i ? { ...p, k } : p)));
  const setVal = (i: number, v: string) => emit(pairs.map((p, idx) => (idx === i ? { ...p, v } : p)));
  const addRow = () => setPairs([...pairs, createPair()]);
  const removeRow = (i: number) => emit(pairs.filter((_, idx) => idx !== i));

  return (
    <FormControl disabled={disabled} required={isRequired}>
      {label && <InputLabel shrink>{label}</InputLabel>}
      <Box sx={{ ...flex.col, gap: '10px' }}>
        {pairs.map((p, i) => (
          <Box key={p.id} sx={{ ...flex.itemsCenter }}>
            <TextInput
              size="small"
              placeholder={keyPlaceholder}
              value={p.k}
              sx={{ width: 255 }}
              onChange={(val: string) => setKey(i, val)}
              disabled={disabled}
            />
            {fieldArrow}
            <TextInput
              size="small"
              placeholder={valuePlaceholder}
              value={p.v}
              sx={{ width: 255 }}
              onChange={(val: string) => setVal(i, val)}
              disabled={disabled}
            />
            <IconButton
              size="small"
              aria-label="Remove row"
              disabled={disabled || isLastRow}
              onClick={() => removeRow(i)}
              sx={{ ml: '8px' }}>
              <MinusCircle />
            </IconButton>
          </Box>
        ))}
      </Box>
      <Button variant="outlined" size="xSmall" onClick={addRow} disabled={disabled} sx={{ alignSelf: 'flex-start', mt: 2 }}>
        {plusIcon} {addButtonText}
      </Button>
    </FormControl>
  );
};

export default Dictionary;
